import React, { useCallback } from 'react';
import { getReviewsRatingTotal } from 'features/reviews/selectors';
import { Rating as FrontonRating, RatingProps } from 'fronton-react';
import { isMoscowRegion } from 'utils/is-moscow-region';
import { getOtzyvyUrl } from 'utils/get-otzyvy-url';
import { connect } from 'react-redux';
import type { Dispatch, Action } from 'redux';
import { RootReducer } from 'store/root-reducer';
import { ReviewsValidations } from 'utils/domain-specifics/reviews/validation';
import { getRegionId } from 'features/page-model/ducks/get-model';
import { analyticsUtils, getLang } from 'utils';
import { Styles } from './styles';

export const DATA_QA_OPEN_AS_BLANK = 'open-as-blank';
export const DATA_QA_NOT_OPEN_AS_BLANK = 'not-open-as-blank';

export type ReviewsTotalScoreMappedState = {
  totalScore: number | null;
  reviewsCount: number | null;
  isMoscow: boolean;
};

export type ReviewsTotalScoreProps = ReviewsTotalScoreMappedState & React.HTMLAttributes<HTMLDivElement>;

export const ReviewsTotalScore: React.FC<ReviewsTotalScoreProps> = ({
  reviewsCount,
  totalScore,
  isMoscow,
  className = '',
  ...htmlProps
}) => {
  const lang = getLang();
  const clickHandler = useCallback(() => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementBlock: 'productDetail',
      elementName: 'productRating',
      elementText: LabelsContent.getReviewsText(reviewsCount || 0, lang),
      elementType: 'button',
    });
  }, [reviewsCount]);

  const reviewsRedirect = getOtzyvyUrl(isMoscow, reviewsCount);

  let content = <LabelsContent.RatingStars totalScore={0} reviewsCount={0} />;
  if (
    typeof totalScore === 'number' &&
    ReviewsValidations.isRatingValueValid(totalScore) &&
    typeof reviewsCount === 'number' &&
    ReviewsValidations.isNumberOfRatingsValid(reviewsCount) &&
    reviewsCount > 0
  ) {
    content = <LabelsContent.RatingStars totalScore={totalScore} reviewsCount={reviewsCount} />;
  }

  return (
    <div className={className} {...htmlProps}>
      <a
        href={reviewsRedirect}
        className={Styles.toReviewsLink}
        onClick={clickHandler}
        data-qa={isMoscow ? DATA_QA_OPEN_AS_BLANK : DATA_QA_NOT_OPEN_AS_BLANK}
      >
        {content}
      </a>
    </div>
  );
};

export const mapStoreToProps = (store: RootReducer): ReviewsTotalScoreMappedState => {
  const totalScoreMeta = getReviewsRatingTotal(store);
  const regionCode = getRegionId(store);
  const isMoscow = isMoscowRegion(regionCode);

  return {
    totalScore: totalScoreMeta?.score ?? null,
    reviewsCount: totalScoreMeta?.count ?? null,
    isMoscow,
  };
};

export const PdpReviewsTotalScore = connect(mapStoreToProps)(
  ({
    dispatch,
    ...restProps
  }: { dispatch: Dispatch<Action> } & React.ComponentProps<typeof ReviewsTotalScore>) => (
    <ReviewsTotalScore {...restProps} />
  ),
);

export namespace LabelsContent {
  const reviewsPluralRule = new Intl.PluralRules('ru-RU');
  const reviewsPluralForm = new Map<Intl.LDMLPluralRule, string>([
    ['one', 'отзыв'],
    ['zero', 'отзывов'],
    ['two', 'отзыва'],
    ['few', 'отзыва'],
    ['many', 'отзывов'],
    ['other', 'отзывов'],
  ]);

  export const getReviewsText = (count: number, locale: string) =>
    count
      ? `${count} ${locale === 'kk' ? 'пікір' : reviewsPluralForm.get(reviewsPluralRule.select(count))}`
      : `${locale === 'kk' ? 'Пікірлер жоқ' : 'Нет отзывов'}`;

  export const StarsLabel: React.FC<{ text: string }> = ({ text }) => {
    return (
      <span className={Styles.addReviewsLabel} data-qa="total-score-at-showcase-label_mf-pdp">
        {text}
      </span>
    );
  };

  export const RatingStars: React.FC<{
    totalScore: number;
    reviewsCount: number;
  }> = ({ totalScore, reviewsCount }) => {
    const lang = getLang();
    return (
      <Rating
        value={totalScore}
        color={reviewsCount ? 'control-accent' : 'text-minor'}
        size="l"
        data-qa="total-score-at-showcase_mf-pdp"
      >
        <StarsLabel text={getReviewsText(reviewsCount, lang)} />
      </Rating>
    );
  };

  const Rating: React.FC<RatingProps> = ({ value, ...props }) => {
    const ref = React.createRef<HTMLDivElement>();

    React.useEffect(() => {
      const el = ref.current;
      if (el) {
        el.classList.add(Styles.hidden);
        void el.offsetHeight;
        el.classList.remove(Styles.hidden);
      }
    }, [value, ref]);

    return (
      <div ref={ref}>
        <FrontonRating value={value} {...props} />
      </div>
    );
  };
}
